import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useTask } from '../../context/TaskContext';
import Breadcrumb from '../common/Breadcrumb';
import Pagination from '../common/Pagination';
import { useCompanyWorkForce } from '../../context/CompanyWorkForceContext';
import { PlusIcon } from 'lucide-react';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const TaskList = () => {
  const navigate = useNavigate();
  const { tasks, isLoading, deleteTask } = useTask();
  const [searchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    recurring: '',
    dateRange: 'all'
  });
  const { getWorkForceById } = useCompanyWorkForce();
  const [currentPage, setCurrentPage] = useState(1);
  const paginationLimit = parseInt(process.env.REACT_APP_PAGINATION_LIMIT) || 10;
  const [sortConfig, setSortConfig] = useState({
    key: 'startDate',
    direction: 'desc'
  });

  const handleDelete = async (taskId) => {

    Swal.fire({
        title: "Are you sure?",
        text: "Once deleted, you will not be able to recover this task!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!"
      }).then((result) => {
        if (result.isConfirmed) {
            deleteTask(taskId);
        }
      });
 
  };

  const handleEdit = (taskId) => {
    navigate(`/tasks/edit/${taskId}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getTaskTypeLabel = (type) => {
    const types = {
      call: 'Call',
      email: 'Email',
      misc: 'Misc'
    };
    return types[type] || type;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredTasks = useMemo(() => {
    let result = tasks.map(task => ({
      ...task,
      assignTo: task.assignTo.map(value => getWorkForceById(value))
    })).filter(task => {
      const matchesSearch = 
        getTaskTypeLabel(task.type).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (Array.isArray(task.assignTo) 
          ? task.assignTo.some(assignee => 
              assignee?.toLowerCase().includes(searchTerm.toLowerCase())
            )
          : String(task.assignTo).toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesType = !filters.type || task.type === filters.type;
      const matchesRecurring = filters.recurring === '' || task.recurring === (filters.recurring === 'yes');

      let matchesDate = true;
      if (filters.dateRange !== 'all') {
        const today = new Date();
        const taskDate = new Date(task.startDate);
        const diffTime = Math.abs(taskDate - today);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        switch (filters.dateRange) {
          case 'today':
            matchesDate = diffDays === 0;
            break;
          case 'week':
            matchesDate = diffDays <= 7;
            break;
          case 'month':
            matchesDate = diffDays <= 30;
            break;
          default:
            matchesDate = true;
        }
      }

      return matchesSearch && matchesType && matchesRecurring && matchesDate;
    });
     
    // Sort the filtered results
    return [...result].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle date values
      if (sortConfig.key === 'startDate' || sortConfig.key === 'endDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [tasks, searchTerm, filters, sortConfig, getWorkForceById]);

  const paginatedTasks = useMemo(() => {
    const startIndex = (currentPage - 1) * paginationLimit;
    const endIndex = startIndex + paginationLimit;
    return filteredTasks.slice(startIndex, endIndex);
  }, [filteredTasks, currentPage, paginationLimit]);

  const totalPages = Math.ceil(filteredTasks.length / paginationLimit);

  const SortableHeader = ({ columnKey, children }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
      onClick={() => handleSort(columnKey)}
    >
      <div className="flex items-center">
        {children}
        {sortConfig.key === columnKey && (
          <span className="ml-1">
            {sortConfig.direction === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
 
  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
        <button
          onClick={() => navigate('/tasks/new')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create New Task
        </button>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
         
          <div className="bg-white p-4 rounded-lg shadow space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Task Type</label>
                <select
                  value={filters.type}
                  onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                >
                  <option value="">All Types</option>
                  <option value="call">Call</option>
                  <option value="email">Email</option>
                  <option value="misc">Misc</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Recurring</label>
                <select
                  value={filters.recurring}
                  onChange={(e) => setFilters(prev => ({ ...prev, recurring: e.target.value }))}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                >
                  <option value="">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Date Range</label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                </select>
              </div>
            </div>
          </div>
        
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader columnKey="type">Type</SortableHeader>
              <SortableHeader columnKey="startDate">Start Date</SortableHeader>
              <SortableHeader columnKey="endDate">End Date</SortableHeader>
              <SortableHeader columnKey="endTime">End Time</SortableHeader>
              <SortableHeader columnKey="assignTo">Assign To</SortableHeader>
              <SortableHeader columnKey="recurring">Recurring</SortableHeader>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedTasks.map((task) => (
              <tr key={`task-${task.id}`} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    task.type === 'call' ? 'bg-blue-100 text-blue-800' :
                    task.type === 'email' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {getTaskTypeLabel(task.type)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDate(task.startDate)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDate(task.endDate)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatTime(task.endTime)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {Array.isArray(task.assignTo)
                  ? task.assignTo.map((value) => `${value?.first_name} ${value?.last_name}`).join(', ')
                  : 'unassigned'}

                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {task.recurring ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      No
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(task.id)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(task.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {paginatedTasks.length === 0 && (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks Found</h3>
                      <p className="text-gray-500 mb-4">Get started by creating a new task</p>
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {paginatedTasks.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
};

export default TaskList; 